import { apiClient } from '../../utils/apiClient';
import dashboardService from '../../services/dashboardService';
import authStorage from '../../utils/authStorage';
import { 
  ErrorClassifier, 
  RetryManager, 
  CircuitBreaker, 
  ERROR_TYPES 
} from '../../utils/errorHandling';

// Circuit breakers for service reliability
const assignmentCircuitBreaker = new CircuitBreaker('AssignmentService', {
  failureThreshold: 3,
  resetTimeout: 30000 // 30 seconds
});

const claimsCircuitBreaker = new CircuitBreaker('ClaimsService', {
  failureThreshold: 5,
  resetTimeout: 60000 // 1 minute
});
const getAllClaims = async (options = {}) => {
  const { signal, page = 1, limit = 10, assignedTo, submittedBy, forceRefresh = false } = options;
  
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (assignedTo) {
      queryParams.append('assignedTo', assignedTo);
    }
    
    if (submittedBy) {
      queryParams.append('submittedBy', submittedBy);
    }
    
    if (!assignedTo && !submittedBy) {
    }
    
    // Prepare request options
    const requestOptions = { signal };
    
    // Add cache-busting if force refresh is requested
    if (forceRefresh) {
      requestOptions.noCache = true;
    }
    
    const response = await apiClient.get(`claims/?${queryParams}`, requestOptions);
    
    // Handle the nested response structure from apiClient
    const data = response.data;
    
    
    // Backend returns claims in response.data with pagination info
    // New backend format: { data: [...claims...], total, page, limit, hasMore }
    return {
      claims: data.data || [],
      pagination: {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        hasMore: data.hasMore || false
      }
    };
  } catch (error) {
    console.error('❌ [ClaimService] Error fetching all claims:', error);
    if (error.name === 'AbortError') {
      throw error; // Re-throw abort errors to be handled by caller
    }
    
    // Handle 304 Not Modified specifically
    if (error.status === 304 && error.isNotModified) {
      throw new Error('Content not modified - use cached data');
    }
    
    throw error;
  }
};

// Get claim by ID
const getClaimById = async (claimId, options = {}) => {
  const { signal } = options;
  
  try {
    // Request detailed field level to include financial information and submittedAt
    const response = await apiClient.get(`claims/${claimId}?fields=detailed&expand=documents,auditLogs`, { signal });
    
    // Extract claim from standardized response envelope
    const claimData = response.data.data;
    
    // Log warning in development if essential fields are missing
    if (import.meta.env.DEV) {
      if (!claimData.submittedAt) {
        console.warn(`ClaimService: Missing 'submittedAt' field for claim ${claimId}`);
      }
      if (!claimData.documents && !Array.isArray(claimData.documents)) {
        console.warn(`ClaimService: Missing or invalid 'documents' field for claim ${claimId}`);
      }
      if (!claimData.sla) {
        console.warn(`⚠️ ClaimService: Missing SLA data for claim ${claimId}`);
      }
    }
    
    return claimData;
  } catch (error) {
    console.error(`❌ [ClaimService] Error fetching claim ${claimId}:`, error);
    if (error.name === 'AbortError') {
      throw error;
    }
    throw error;
  }
};

// Create new claim
const createClaim = async (claimData) => {
  // Map frontend form fields to backend expected fields
  const backendData = {
    // Required fields for all claims
    type: claimData.type,  // Updated field name
    policyNumber: claimData.member_number,  // Map member_number to policyNumber  
    claimantName: claimData.claimantName,  // Updated field name
    claimantMobile: claimData.claimant_mobile,
    claimantAddress: claimData.claimant_address,
    
    // Add submitted_by tracking - use canonical field name
    submittedById: claimData.submittedById,
    
    // Death claim specific fields
    ...(claimData.type === 'DEATH' && {
      deceasedName: claimData.beneficiary_name,
      deceasedDateOfDeath: claimData.incident_date,
      relationshipToDeceased: claimData.beneficiary_relationship,
      causeOfDeath: claimData.incident_description,
    }),
    
    // Disability claim specific fields  
    ...(claimData.type === 'DISABILITY' && {
      disabilityType: claimData.disability_type === 'TEMPORARY' ? 'TTD' : 'PTD',
      dateOfInjury: claimData.incident_date,
      medicalProvider: claimData.hospital_name,
    }),
    
    // Critical illness specific fields
    ...(claimData.type === 'CRITICAL_ILLNESS' && {
      diagnosis: claimData.diagnosis,
      diagnosisDate: claimData.incident_date,
      illnessStage: claimData.treatment_details,
    }),
    
    // Financial information
    bankAccountName: claimData.beneficiary_name || claimData.claimantName,
    bankName: claimData.bankName,
    accountNumber: claimData.accountNumber,
    branch: claimData.branch,
    benefitAmount: parseFloat(claimData.benefitAmount) || 0,  // Updated field name
  };

  // Console log removed for production
  // Console log removed for production

  const response = await apiClient.post('claims/', backendData);
  // Console log removed for production
  
  // Invalidate dashboard cache since a new claim was created
  dashboardService.invalidateCache();
  
  // Handle nested response structure
  const data = response.data;
  if (data && data.data) {
    return data.data;
  }
  return data;
};

// Update claim
const updateClaim = async (claimId, claimData) => {
  const response = await apiClient.put(`claims/${claimId}`, claimData);
  
  // Invalidate dashboard cache since a claim was updated
  dashboardService.invalidateCache();
  
  // Handle nested response structure
  const data = response.data;
  if (data && data.data) {
    return data.data;
  }
  return data;
};

// Delete claim
const deleteClaim = async (claimId) => {
  const response = await apiClient.delete(`claims/${claimId}`);
  return response.data;
};

// Get claims by status
const getClaimsByStatus = async (status, options = {}) => {
  const { signal, page = 1, limit = 10, assignedTo, submittedBy, forceRefresh = false } = options;
  
  try {
    const queryParams = new URLSearchParams({
      status: status,
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (assignedTo) {
      queryParams.append('assignedTo', assignedTo);
    }
    
    if (submittedBy) {
      queryParams.append('submittedBy', submittedBy);
    }
    
    if (!assignedTo && !submittedBy) {
    }
    
    // Prepare request options
    const requestOptions = { signal };
    
    // Add cache-busting if force refresh is requested
    if (forceRefresh) {
      requestOptions.noCache = true;
    }
    
    const response = await apiClient.get(`claims?${queryParams}`, requestOptions);
    
    // Handle the nested response structure from apiClient
    const data = response.data;
    
    // Backend returns claims in response.data with pagination info
    // New backend format: { data: [...claims...], total, page, limit, hasMore }
    return {
      claims: data.data || data.claims || [],
      pagination: data.pagination || {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        hasMore: data.hasMore || false
      }
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    
    // Handle 304 Not Modified specifically
    if (error.status === 304 && error.isNotModified) {
      throw new Error('Content not modified - use cached data');
    }
    
    throw error;
  }
};

// Update claim status
const updateClaimStatus = async (claimId, status, comment = '') => {
  
  try {
    const token = authStorage.getToken();

    const endpoint = `claims/${claimId}/status`;
    const payload = { status, comment };
    
    
    // Use the general status update endpoint
    const response = await apiClient.patch(endpoint, payload);
    
    
    // Invalidate dashboard cache since claim status changed
    dashboardService.invalidateCache();
    
    // Handle nested response structure
    const data = response.data;
    if (data && data.data) {
      return data.data;
    }
    return data;
  } catch (error) {
    console.error('❌ [ClaimService] updateClaimStatus error:', {
      claimId,
      status,
      comment,
      error: error.message,
      errorName: error.name,
      errorStack: error.stack,
      response: error.response?.data || 'No response data'
    });
    throw error;
  }
};

/**
 * Review a claim and mark it as ready for approval
 * @param {string|number} claimId - ID of the claim to review
 * @param {string} comments - Review comments
 * @returns {Promise<Object>} - Updated claim data
 */
const reviewClaim = async (claimId, comments) => {
  
  if (!claimId) {
    throw new Error('Claim ID is required');
  }
  
  try {
    const response = await apiClient.patch(`claims/${claimId}/review`, { comments });
    
    
    // Handle nested response structure
    const data = response.data;
    return data.claim || data;
  } catch (error) {
    console.error('❌ [ClaimService] Error reviewing claim:', error);
    throw error;
  }
};

/**
 * Recommend a claim for approval (Claims Officer action)
 * @param {string|number} claimId - ID of the claim to recommend
 * @param {Object} recommendationData - Recommendation details
 * @param {string} recommendationData.recommendationNotes - Required notes explaining the recommendation
 * @param {string} [recommendationData.additionalInfo] - Optional additional information
 * @returns {Promise<Object>} - Updated claim data
 */
const recommendClaimForApproval = async (claimId, recommendationData) => {
  
  if (!claimId) {
    throw new Error('Claim ID is required');
  }
  
  if (!recommendationData.notes) {
    throw new Error('Recommendation notes are required');
  }
  
  try {
    const response = await apiClient.post(`claims/${claimId}/recommend`, recommendationData);
    
    
    // Invalidate dashboard cache since claim status changed
    dashboardService.invalidateCache();
    
    // Handle nested response structure
    const data = response.data;
    return data;
  } catch (error) {
    console.error('❌ [ClaimService] Error recommending claim:', error);
    throw error;
  }
};

/**
 * Approve or reject a claim (Approver action)
 * @param {string|number} claimId - ID of the claim to approve/reject
 * @param {Object} approvalData - Approval decision details
 * @param {string} approvalData.decision - Either 'APPROVE' or 'REJECT'
 * @param {string} approvalData.approvalNotes - Required notes explaining the decision
 * @param {string} [approvalData.rejectionReason] - Required if decision is 'REJECT'
 * @returns {Promise<Object>} - Updated claim data
 */
const approveClaim = async (claimId, approvalData) => {
  
  if (!claimId) {
    throw new Error('Claim ID is required');
  }
  
  if (!approvalData.decision || !['APPROVE', 'REJECT'].includes(approvalData.decision)) {
    throw new Error('Valid decision (APPROVE or REJECT) is required');
  }
  
  if (!approvalData.approvalNotes) {
    throw new Error('Approval notes are required');
  }
  
  try {
    const response = await apiClient.post(`claims/${claimId}/approve`, approvalData);
    
    
    // Invalidate dashboard cache since claim status changed
    dashboardService.invalidateCache();
    
    // Handle nested response structure
    const data = response.data;
    return data;
  } catch (error) {
    console.error('❌ [ClaimService] Error processing claim approval:', error);
    throw error;
  }
};

/**
 * Authorize payment for approved claim (Finance Officer only)
 * @param {string|number} claimId - ID of the claim
 * @param {Object} paymentData - Payment authorization data
 * @param {string} paymentData.authorizationNotes - Notes explaining payment authorization
 * @param {string} [paymentData.paymentMethod] - Payment method (default: BANK_TRANSFER)
 * @returns {Promise<Object>} - Payment authorization result
 */
const authorizePayment = async (claimId, paymentData) => {
  
  if (!claimId) {
    throw new Error('Claim ID is required');
  }
  
  if (!paymentData.authorizationNotes) {
    throw new Error('Authorization notes are required');
  }
  
  try {
    const response = await apiClient.post(`claims/${claimId}/authorize-payment`, paymentData);
    
    
    // Invalidate dashboard cache since claim status changed
    dashboardService.invalidateCache();
    
    // Handle nested response structure
    const data = response.data;
    return data;
  } catch (error) {
    console.error('❌ [ClaimService] Error authorizing payment:', error);
    throw error;
  }
};

// Get claims by user ID
const getClaimsByUserId = async (userId, params = {}, options = {}) => {
  if (!userId) {
    throw new Error('User ID is required');
  }
  
  const { signal } = options;
  
  
  try {
    const queryParams = new URLSearchParams({
      userId: userId.toString(),
      ...params
    });
    
    const response = await apiClient.get(`claims/user?${queryParams}`, { signal });
    
    
    // Handle the nested response structure from apiClient
    const data = response.data;
    
    // Backend returns claims in response.data with pagination info
    // New backend format: { data: [...claims...], total, page, limit, hasMore }
    const result = {
      claims: data.data || data.claims || [],
      pagination: data.pagination || {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        hasMore: data.hasMore || false
      },
      meta: data.meta || {}
    };
    
    
    return result;
  } catch (error) {
    console.error('❌ [claimService] API call failed:', {
      userId,
      error: error.message,
      status: error.response?.status,
      responseData: error.response?.data
    });
    if (error.name === 'AbortError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Assign a claim to current user (Claims Officer takes ownership)
 * @param {string|number} claimId - ID of the claim to assign
 * @param {string} [comment] - Optional comment explaining the assignment
 * @returns {Promise<Object>} - Assignment result data
 */
const assignClaimToSelf = async (claimId, comment = '') => {
  
  if (!claimId) {
    throw ErrorClassifier.classifyError(
      new Error('Claim ID is required'),
      { operation: 'assignClaimToSelf', claimId }
    );
  }
  
  // Permission validation - only Claims Officers can assign claims to themselves
  // Note: Backend will also validate, but this provides immediate UX feedback
  const user = authStorage.getUser() || {};
  const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
  
  if (!userRoles.includes('Claims_Officer')) {
    throw ErrorClassifier.classifyError(
      new Error('Only Claims Officers can assign claims to themselves'),
      { operation: 'assignClaimToSelf', claimId, userRoles }
    );
  }
  
  try {
    return await assignmentCircuitBreaker.execute(async () => {
      return await RetryManager.executeWithRetry(async () => {
        const payload = {
          action: 'take',
          ...(comment && { comment })
        };
        
        const response = await apiClient.patch(`claims/${claimId}/assign`, payload);
        
        
        // Invalidate dashboard cache since claim assignment changed
        dashboardService.invalidateCache();
        
        // Handle nested response structure
        const data = response.data;
        return data;
      }, { operation: 'assignClaimToSelf', claimId });
    }, { operation: 'assignClaimToSelf', claimId });
  } catch (error) {
    console.error('❌ [ClaimService] Error assigning claim:', error);
    throw ErrorClassifier.classifyError(error, { 
      operation: 'assignClaimToSelf', 
      claimId,
      comment
    });
  }
};

/**
 * Release a claim from current user (Claims Officer releases ownership)
 * @param {string|number} claimId - ID of the claim to release
 * @param {string} [comment] - Optional comment explaining the release
 * @returns {Promise<Object>} - Release result data
 */
const releaseClaimFromSelf = async (claimId, comment = '') => {
  
  if (!claimId) {
    throw ErrorClassifier.classifyError(
      new Error('Claim ID is required'),
      { operation: 'releaseClaimFromSelf', claimId }
    );
  }
  
  // Permission validation - only Claims Officers can release claims from themselves
  // Note: Backend will also validate, but this provides immediate UX feedback
  const user = authStorage.getUser() || {};
  const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
  
  if (!userRoles.includes('Claims_Officer')) {
    throw ErrorClassifier.classifyError(
      new Error('Only Claims Officers can release claims from themselves'),
      { operation: 'releaseClaimFromSelf', claimId, userRoles }
    );
  }
  
  try {
    return await assignmentCircuitBreaker.execute(async () => {
      return await RetryManager.executeWithRetry(async () => {
        const payload = {
          action: 'release',
          ...(comment && { comment })
        };
        
        const response = await apiClient.patch(`claims/${claimId}/assign`, payload);
        
        
        // Invalidate dashboard cache since claim assignment changed
        dashboardService.invalidateCache();
        
        // Handle nested response structure
        const data = response.data;
        return data;
      }, { operation: 'releaseClaimFromSelf', claimId });
    }, { operation: 'releaseClaimFromSelf', claimId });
  } catch (error) {
    console.error('❌ [ClaimService] Error releasing claim:', error);
    throw ErrorClassifier.classifyError(error, { 
      operation: 'releaseClaimFromSelf', 
      claimId,
      comment
    });
  }
};

const claimService = {
  getAllClaims,
  getClaimById,
  getClaimsByUserId,
  createClaim,
  updateClaim,
  deleteClaim,
  getClaimsByStatus,
  updateClaimStatus,
  reviewClaim,
  recommendClaimForApproval,
  approveClaim,
  authorizePayment,
  assignClaimToSelf,
  releaseClaimFromSelf
};

export default claimService;
